import { MdCheck, MdClose } from "react-icons/md";
import Modal from "../Modal/Modal";
import { EButtonKind } from "../Button/ButtonTypes";
import { ICON_SIZES } from "src/helpers/constants";
import { useTranslation } from "react-i18next";
import { ICustomSelectOption } from "../Select/CustomSelect";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import InfoBlock from "../InfoBlock/InfoBlock";
import RadioGrid from "../RadioGroup/RadioGrid";
import useMobile from "src/hooks/useMobile";
import CheckBoxGrid from "../CheckBoxGroup/CheckBoxGrid";
import TableQuickFilter from "../TableQuickFilter/TableQuickFilter";

interface ISelectModal {
  onClose: () => void;
  options: ICustomSelectOption[];
  isMulti?: boolean;
  onSelect: (e: any) => void;
  value: any;
}

const normalize = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const SelectModal: React.FC<ISelectModal> = ({
  onClose,
  options,
  isMulti = false,
  value,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [newSelectedValue, setNewSelectedValue] = useState<any>(
    isMulti ? value : value?.value
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredResults, setFilteredResults] =
    useState<ICustomSelectOption[]>(options);

  const { isMobile } = useMobile();

  const filterResults = useCallback(() => {
    const term = normalize(searchTerm || "");

    if (term) {
      setFilteredResults(
        options?.filter?.((option: ICustomSelectOption) => {
          const label = normalize(`${option?.label || ""}`);
          const value = normalize(`${option?.value || ""}`);
          return label.includes(term) || value.includes(term);
        })
      );
    } else {
      setFilteredResults(options);
    }
  }, [searchTerm, options]);

  useEffect(() => {
    filterResults();
  }, [searchTerm]);

  const selectedCountMessage = useMemo(() => {
    if (isMulti) {
      const total = (newSelectedValue as any[])?.length;

      if (total === 1) {
        return `1 elemento seleccionado`;
      }

      return `${total} elementos seleccionados`;
    }

    return null;
  }, [newSelectedValue]);

  return (
    <Modal
      onEscape={() => onClose()}
      className="md:!max-w-[680px] md:!min-w-[280px] md:w-full"
    >
      <Modal.Header
        onClose={() => onClose()}
        title={
          isMulti
            ? "Selecciona uno o varios elementos"
            : "Selecciona un elemento"
        }
      />
      <Modal.Body>
        <div className={clsx(["flex", "w-full", "flex-col", "gap-4"])}>
          {options?.length ? (
            <div className={clsx(["flex", "gap-4", "flex-col", "w-full"])}>
              <TableQuickFilter
                onChange={setSearchTerm}
                searchTerm={searchTerm}
              />

              {filteredResults?.length ? (
                <>
                  {isMulti ? (
                    <CheckBoxGrid
                      columns={isMobile ? 1 : 2}
                      onChange={(e: any) => {
                        setNewSelectedValue(e);
                      }}
                      options={filteredResults}
                      selected={newSelectedValue as any[]}
                    />
                  ) : (
                    <RadioGrid
                      columns={isMobile ? 1 : 2}
                      onChange={(value: any) => {
                        if (isMulti) {
                          setNewSelectedValue(value);
                        } else {
                          if (value === newSelectedValue) {
                            setNewSelectedValue("");
                          } else {
                            setNewSelectedValue(value);
                          }
                        }
                      }}
                      options={filteredResults}
                      selected={newSelectedValue as string}
                    />
                  )}
                </>
              ) : (
                <InfoBlock
                  message="Tu búsqueda no produjo resultados. Intenta con otro término."
                  title="Sin resultados"
                />
              )}
            </div>
          ) : (
            <InfoBlock
              message={`No hay elementos previamente guardados. Debes agregar manualmente ${
                isMulti ? "los valores" : "el valor"
              } del campo.`}
              title="Lista vacía"
            />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer
        actions={[
          {
            icon: <MdCheck size={ICON_SIZES.MD} />,
            onClick: () => onSelect(newSelectedValue),
            kind: EButtonKind.SAVE,
            text: t("Seleccionar"),
            disabled: !newSelectedValue,
            hide: options?.length === 0,
          },
          {
            text: t("common_buttons.close"),
            onClick: () => onClose(),
            kind: EButtonKind.NORMAL,
            icon: <MdClose size={ICON_SIZES.MD} />,
          },
        ]}
      />
    </Modal>
  );
};

export default SelectModal;
